import { useCallback, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  Button,
  Input,
  Text,
  Group,
  TextInput,
  Select,
  Tooltip,
  Box,
  LoadingOverlay,
  type BoxProps
} from '@mantine/core';

import type { AddressType } from '../../../types/miscTypes';
import { US_STATE_SELECT_OPTIONS } from '../../../utils/addressUtils';

export type FormValues = {
  phone: string
  ein: string
  email: string
  authorizedRep: string
  name: string
} & AddressType;

const defaultPlaceholders: Record<keyof FormValues, string | undefined> = {
  name: 'Company Name',
  address1: 'Company Business Address',
  address2: 'Company Suite #',
  phone: 'Phone Number',
  city: undefined,
  ein: undefined,
  state: undefined,
  zipCode: undefined,
  email: undefined,
  authorizedRep: undefined
};

type Props = {
  onSubmit: (values: FormValues) => void
  initialValues?: FormValues
  placeholderValues?: FormValues
  canEdit?: boolean
  loading?: boolean
  lockedFields?: Array<keyof FormValues>
} & BoxProps;

const defaultInitialValues: FormValues = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  ein: '',
  email: '',
  authorizedRep: '',
  name: ''
};

const CompanyDetailsForm: React.FC<Props> = (props: Props) => {
  const {
    onSubmit,
    initialValues,
    placeholderValues,
    canEdit = false,
    loading = false,
    lockedFields = [],
    ...boxProps
  } = props;

  const form = useForm({
    initialValues: initialValues !== undefined
      ? { ...defaultInitialValues, ...initialValues }
      : defaultInitialValues,
    validate: {
      phone: (value: string) => (
        value === '' || /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(value)
          ? null
          : 'Please input a valid phone number'
      )
    }
  });

  useEffect(() => {
    form.setInitialValues(initialValues ?? defaultInitialValues);
    form.reset();
  }, [initialValues]);

  const getInputProps = useCallback((fieldName: keyof FormValues) => ({
    disabled: !canEdit || lockedFields.includes(fieldName),
    placeholder: placeholderValues === undefined || !(fieldName in placeholderValues)
      ? defaultPlaceholders[fieldName]
      : placeholderValues[fieldName],
    ...form.getInputProps(fieldName)
  }), [lockedFields, canEdit, placeholderValues, form.getInputProps]);

  return (
    <Box {...boxProps} >
      <LoadingOverlay visible={loading} />
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Tooltip
          events={{
            hover: lockedFields.includes('name'),
            focus: false,
            touch: false
          }}
          label='Need to edit? Contact us at info@ensis.ai'
        >
          <TextInput
            label='Company Name'
            w={548}
            mb={16}
            {...getInputProps('name')}
          />
        </Tooltip>
        <TextInput
          label='Street Address'
          w={548}
          mb={16}
          {...getInputProps('address1')}
        />
        <TextInput
          label='Unit, suite, etc'
          w={548}
          mb={16}
          {...getInputProps('address2')}
        />
        <Group>
          <TextInput
            label='City'
            w={329}
            mb={16}
            {...getInputProps('city')}
          />
          <Select
            label='State'
            allowDeselect={false}
            w={116}
            mb={16}
            styles={{
              dropdown: {
                borderRadius: 4
              }
            }}
            data={US_STATE_SELECT_OPTIONS}
            {...getInputProps('state')}
          />
        </Group>
        <Group mb={16}>
          <TextInput
            label='Postal Code'
            w={116}
            {...getInputProps('zipCode')}
          />
          <Input.Wrapper label='Phone Number' {...getInputProps('phone')}>
            <Group>
              <Text inline>
                +1
              </Text>
              <Input
                {...getInputProps('phone')}
              />
            </Group>
          </Input.Wrapper>
          <TextInput
            label='EIN'
            w={116}
            {...getInputProps('ein')}
          />
        </Group>
        <TextInput
          label='Company Email'
          w={548}
          mb={16}
          {...getInputProps('email')}
        />
        <TextInput
          label='Authorized Representative Name'
          w={548}
          mb={24}
          {...getInputProps('authorizedRep')}
        />
        {canEdit && (
          <Button
            loading={loading}
            type="submit"
            w={138}
            mb={16}
            disabled={!form.isDirty() || loading}
          >
            Save
          </Button>
        )}
      </form>
    </Box>);
};

export default CompanyDetailsForm;
